/**
 * 手机验证码登录步骤
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Smartphone, Image, Shield, Loader2, KeyRound, UserPlus } from 'lucide-react';
import authService from '../../../../services/authService';

const normalizeInviteError = (message) => {
    if (!message) return '登录失败，请重试';
    if (/invite|邀请码/i.test(message)) {
        return `邀请码校验失败：${message}`;
    }
    return message;
};

const PhoneLoginStep = ({ onSuccess, onError }) => {
    const [phone, setPhone] = useState('');
    const [captchaCode, setCaptchaCode] = useState('');
    const [smsCode, setSmsCode] = useState('');
    const [inviteCode, setInviteCode] = useState('');

    const [captchaId, setCaptchaId] = useState('');
    const [captchaImage, setCaptchaImage] = useState('');
    const [captchaLoading, setCaptchaLoading] = useState(false);

    const [smsSending, setSmsSending] = useState(false);
    const [smsCountdown, setSmsCountdown] = useState(0);
    const [isRegistered, setIsRegistered] = useState(null);

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const loadCaptcha = useCallback(async () => {
        setCaptchaLoading(true);
        setCaptchaCode('');
        try {
            const response = await authService.getCaptcha();
            if (response.captcha_id && response.captcha_image) {
                setCaptchaId(response.captcha_id);
                setCaptchaImage(response.captcha_image);
            }
        } catch (err) {
            console.error('加载验证码失败:', err);
        } finally {
            setCaptchaLoading(false);
        }
    }, []);

    useEffect(() => {
        loadCaptcha();
    }, [loadCaptcha]);

    useEffect(() => {
        if (smsCountdown > 0) {
            const timer = setTimeout(() => setSmsCountdown(smsCountdown - 1), 1000);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [smsCountdown]);

    const handleSendSms = async () => {
        if (!phone || phone.length !== 11) {
            setError('请输入正确的手机号');
            return;
        }
        if (!captchaCode) {
            setError('请输入图形验证码');
            return;
        }

        setSmsSending(true);
        setError('');

        try {
            const res = await authService.sendSmsForLogin({
                phone,
                captcha_id: captchaId,
                captcha_code: captchaCode,
            });
            setIsRegistered(Boolean(res?.is_registered));
            setSmsCountdown(60);
        } catch (err) {
            setError(err.response?.data?.error || '发送失败，请重试');
            loadCaptcha();
        } finally {
            setSmsSending(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!phone || !smsCode) {
            setError('请填写完整信息');
            return;
        }

        if (isRegistered === false && !inviteCode.trim()) {
            setError('检测到新用户，请先输入邀请码再登录/注册');
            return;
        }

        setSubmitting(true);
        setError('');

        try {
            const response = await authService.loginBySms({
                phone,
                code: smsCode,
                invite_code: inviteCode.trim(),
            });
            onSuccess?.(response);
        } catch (err) {
            const rawError = err.response?.data?.error || '登录失败，请重试';
            const errorMsg = normalizeInviteError(rawError);
            setError(errorMsg);
            onError?.(errorMsg);
        } finally {
            setSubmitting(false);
        }
    };

    const inputClass = 'w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm transition-all focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 focus:bg-white focus:outline-none';

    return (
        <div className="pt-2">
            <div className="text-center mb-5">
                <div className="w-11 h-11 mx-auto mb-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-md shadow-blue-500/20">
                    <Smartphone className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">手机号登录</h3>
                <p className="text-xs text-gray-400 mt-0.5">新用户自动创建账户，首次登录需邀请码</p>
            </div>

            {error && (
                <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-2.5 mb-3 bg-red-50 border border-red-100 rounded-lg text-xs text-red-600"
                >
                    {error}
                </motion.div>
            )}

            {isRegistered === false && (
                <div className="p-2.5 mb-3 bg-amber-50 border border-amber-100 rounded-lg text-xs text-amber-700 flex items-center gap-1.5">
                    <UserPlus className="w-3.5 h-3.5" />
                    <span>检测到该手机号为新用户，请填写邀请码继续。</span>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-2.5">
                <div className="relative">
                    <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400">
                        <Smartphone className="w-4 h-4" />
                    </div>
                    <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
                        placeholder="请输入手机号"
                        className={inputClass}
                        maxLength={11}
                    />
                </div>

                <div className="relative">
                    <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400">
                        <KeyRound className="w-4 h-4" />
                    </div>
                    <input
                        type="text"
                        value={inviteCode}
                        onChange={(e) => setInviteCode(e.target.value.toUpperCase().trim())}
                        placeholder="邀请码（新用户必填）"
                        className={inputClass}
                        maxLength={32}
                    />
                </div>

                <div className="flex gap-2.5">
                    <div className="relative flex-1">
                        <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400">
                            <Image className="w-4 h-4" />
                        </div>
                        <input
                            type="text"
                            value={captchaCode}
                            onChange={(e) => setCaptchaCode(e.target.value)}
                            placeholder="图形验证码"
                            className={inputClass}
                            maxLength={4}
                        />
                    </div>
                    <button
                        type="button"
                        onClick={loadCaptcha}
                        disabled={captchaLoading}
                        className="w-24 h-[46px] bg-gray-100 rounded-xl overflow-hidden border border-gray-200 hover:border-gray-300 transition-all flex items-center justify-center"
                    >
                        {captchaLoading ? (
                            <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                        ) : captchaImage ? (
                            <img src={captchaImage} alt="验证码" className="h-full w-full object-cover" />
                        ) : (
                            <span className="text-xs text-gray-400">点击获取</span>
                        )}
                    </button>
                </div>

                <div className="flex gap-2.5">
                    <div className="relative flex-1">
                        <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400">
                            <Shield className="w-4 h-4" />
                        </div>
                        <input
                            type="text"
                            value={smsCode}
                            onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            placeholder="短信验证码"
                            className={inputClass}
                            maxLength={6}
                        />
                    </div>
                    <button
                        type="button"
                        onClick={handleSendSms}
                        disabled={smsSending || smsCountdown > 0 || !phone || phone.length !== 11}
                        className={`px-4 py-3 rounded-xl text-xs font-medium whitespace-nowrap transition-all ${smsCountdown > 0
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md shadow-blue-500/20'
                            }`}
                    >
                        {smsSending ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : smsCountdown > 0 ? (
                            `${smsCountdown}s`
                        ) : (
                            '获取验证码'
                        )}
                    </button>
                </div>

                <motion.button
                    type="submit"
                    disabled={submitting || !phone || !smsCode}
                    whileHover={{ scale: submitting ? 1 : 1.01 }}
                    whileTap={{ scale: submitting ? 1 : 0.99 }}
                    className={`w-full py-3 rounded-xl font-semibold text-sm text-white transition-all ${submitting || !phone || !smsCode
                        ? 'bg-gray-300 cursor-not-allowed'
                        : 'bg-gradient-to-r from-blue-500 to-indigo-600 shadow-md shadow-blue-500/20'
                        }`}
                >
                    {submitting ? (
                        <div className="flex items-center justify-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span>登录中...</span>
                        </div>
                    ) : (
                        '登录 / 注册'
                    )}
                </motion.button>
            </form>

            <p className="text-center text-[10px] text-gray-400 mt-3">
                登录即表示同意
                <button className="text-blue-500 hover:underline mx-0.5">服务协议</button>
                和
                <button className="text-blue-500 hover:underline mx-0.5">隐私政策</button>
            </p>
        </div>
    );
};

export default PhoneLoginStep;
